import React from 'react';
import core, { App } from '@gin-betting/core';
import { OrderedSet } from 'immutable';

import { ExtendedRouts, LocalRouts } from 'app-constants';
import { NoResults } from 'components';
import { AsyncOutright } from 'modules/outright/async';
import UserDevice from 'services/user-device';
import { TopMenu } from 'views/top-menu';
import ResultsMatchList from '../Results/ResultsMatchList';
import UpcomingMatchList from '../Upcoming/UpcomingMatchList';
import { useFavoriteList } from './useFavoriteList';

import './favoriteList.scss';

const {
    utils: { checkOnRoute },
} = core;

interface IFavoriteList {
    homeTabs: {
        title: JSX.Element;
        link: LocalRouts;
    }[];
}

const FavoriteList: React.FC<IFavoriteList> = ({ homeTabs }) => {
    const {
        currFavoriteIds,
        fetchStatus,
        filteredMatchIds,
        isOutrights,
        msg,
        lessThanOrEqual,
        groupedIdsByTournament,
    } = useFavoriteList();

    const whatToRender = (): React.ReactNode => {
        switch (true) {
            case fetchStatus === App.FetchStatus.LOADED &&
                !filteredMatchIds.count():
                return (
                    <NoResults
                        key="-2"
                        type={App.PageType.LIST}
                        defaultMessage={msg}
                    />
                );
            /* falls through */
            case isOutrights:
                return groupedIdsByTournament.valueSeq().map(group => {
                    const filteredGroup = currFavoriteIds.intersect(
                        group as OrderedSet<string>
                    );

                    return filteredGroup.map(outrightId => (
                        <AsyncOutright
                            key={outrightId}
                            matchId={outrightId}
                            maxSizeOfTables={Infinity}
                        />
                    ));
                });
            /* falls through */
            case checkOnRoute(ExtendedRouts.FAVORITE_RESULTS):
                return (
                    <ResultsMatchList
                        isSmall={lessThanOrEqual.small}
                        matchIds={filteredMatchIds}
                    />
                );
            default:
                return (
                    <UpcomingMatchList
                        isMedium={lessThanOrEqual.medium}
                        isSmall={lessThanOrEqual.small}
                        matchIds={filteredMatchIds}
                    />
                );
        }
    };

    const isMobile = UserDevice.getInstance()?.isDeviceMobile();

    return (
        <div styleName="container" className="__app-FavoriteUpcoming-container">
            <TopMenu
                homeTabs={homeTabs}
                fetchStatus={isMobile ? App.FetchStatus.INITIAL : fetchStatus}
            />
            <div>{whatToRender()}</div>
        </div>
    );
};

export default React.memo(FavoriteList);
